import React from "react";
import {
    Box,
    Button,
    Text,
    Accordion
} from "@mantine/core";
import {modals} from "@mantine/modals";
import {
    IStopDataCollectionStep,
    isProtocolStep
} from "../../../types/protocol";
import {
    IStopDataCollectionStepDefinition
} from "../../../types/protocolDefinitions";
import {ProtocolFileManager} from "../../components/ProtocolFileManager";
import {TSubjectId} from "@/common/types";
import stepClasses from "../StepView.module.css";
import {IconAlertCircle} from "@tabler/icons-react";
import {
    useStopDataCollection
} from "./DataCollection";
import {useNeurotuneStore} from "@/common/store/neurotune.ts";
import {useUploadStatusStore} from "@/protocols/persistence/useUploadStatusStore";
import {TWorkflow} from "../StepView";

type StopDataCollectionStepType = IStopDataCollectionStep | IStopDataCollectionStepDefinition;

export interface StopDataCollectionStepViewProps {
    subjectId?: TSubjectId
    step: StopDataCollectionStepType;
    timezone?: string;
    workflow?: TWorkflow;
}

export const StopDataCollectionStepView: React.FC<StopDataCollectionStepViewProps> = ({
                                                                                          step,
                                                                                          subjectId,
                                                                                          workflow = "session",
                                                                                      }) => {
    const isPlanningMode = workflow === "planning";
    // protocolId is only available on execution steps (not definitions)
    const protocolId = isProtocolStep(step) ? step.protocolId : undefined;
    const activeCompany = useNeurotuneStore((state) => state.activeCompany);
    // Live server-side file count, sourced from ProtocolFileManager's
    // `loadProtocolFiles` response (see the manager's effect). NOT
    // the Rust `summary.persisted` — that's a lifetime cumulative
    // count of every file the worker has finalized, and it does NOT
    // decrement when the user deletes a file via the server, so it
    // shows stale totals like "14 files" when none actually exist.
    const serverFileCount = useUploadStatusStore((s) => s.serverFileCount)
    const setServerFileCount = useUploadStatusStore((s) => s.setServerFileCount)

    const {
        isTauri,
        isStopping,
        hasActiveCollection,
        stopAcquisition,
    } = useStopDataCollection(protocolId, !isPlanningMode);

    const waitingCount =useUploadStatusStore(
        (s) => s.pendingUploads.filter(
            (r) => r.state === 'found',
        ).length,
    );
    // Queued files = rows the watcher has found / copied / made-ready
    // but the worker hasn't picked up yet. The Rust pending_uploads
    // queue is the source of truth now (the legacy daqStatusMap
    // readiness-time list is gone).
    const queuedCount = useUploadStatusStore(
        (s) => s.pendingUploads.filter(
            (r) =>r.state === 'copied' || r.state === 'pending',
        ).length,
    );

    const uploadingCount = useUploadStatusStore(
        (s) => s.pendingUploads.filter((r) => r.state === "uploading").length,
    );


    const handleStop = async () => {
        const hasFilesInProgress = queuedCount > 0 || uploadingCount > 0;

        if (waitingCount >0)  {
            modals.openConfirmModal({
                title: "Warning: Waiting Files",
                size:"xl",
                children: (
                    <Text size="sm">
                        There {waitingCount === 1 ? 'is' : 'are'} {waitingCount} file(s) waiting to
                        be copied from their source directory before upload. Stopping data collection before the wait period is over
                        will leave these files in their source directory and prevent them from being copied and uploaded.
                    </Text>
                ),
                labels: {confirm: "Stop Anyway", cancel: "Cancel"},
                confirmProps: {color: "red"},
                onConfirm: () => { stopAcquisition() },
            });
        } else if (hasFilesInProgress) {
            modals.openConfirmModal({
                title: "Files in Progress",
                children: (
                    <Text size="sm">
                        There {queuedCount === 1 ? 'is' : 'are'} {queuedCount} file(s) queued
                        and {uploadingCount} file(s) currently uploading. Stopping will cancel these uploads.
                    </Text>
                ),
                labels: {confirm: "Stop Anyway", cancel: "Cancel"},
                confirmProps: {color: "red"},
                onConfirm: () => { stopAcquisition() },
            });
        } else {
            await stopAcquisition();
        }
    };

    const buttonText = isStopping ? "Stopping..." : "Stop Data Collection";

    const renderTitleBar = () => {
        return (
            <Box style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                <Text>Collected Files</Text>
                {(waitingCount > 0 || queuedCount > 0 || uploadingCount > 0) ? (
                    <Box style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        backgroundColor: "var(--bios-color-warning-light)",
                        padding: "0.125em 0.25em",
                        borderRadius: "0.25em"
                    }}>
                        <IconAlertCircle size="0.95em" color="var(--bios-color-warning)"/>
                        <Text size="xs" c="dimmed">
                            {waitingCount} waiting, {queuedCount} queued, {uploadingCount} uploading
                        </Text>
                    </Box>
                ) : (
                    <Box style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        backgroundColor: "var(--mantine-color-green-0)",
                        padding: "0.125em 0.25em",
                        borderRadius: "0.25em"
                    }}>
                        <Text size="xs" c="green">
                            {serverFileCount} files
                        </Text>
                    </Box>
                )}
            </Box>
        )

    }

    const renderSessionMode = () => {
        return (
            <Accordion defaultValue="files" variant="contained">
                <Accordion.Item value="files">
                    <Accordion.Control style={{
                        backgroundColor: "var(--bios-medium-blue-1)",
                        borderBottomRightRadius: 0,
                        borderBottomLeftRadius: 0
                    }}>
                        {renderTitleBar()}
                    </Accordion.Control>
                    <Accordion.Panel style={{backgroundColor: "var(--bios-medium-blue-1)"}}>
                        <Box style={{display: "grid", grid: "auto / 1fr", rowGap: "1em"}}>
                            <Button
                                fullWidth
                                size="compact-xs"
                                disabled={!isTauri || isPlanningMode || !hasActiveCollection || isStopping}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleStop();
                                }}
                            >
                                {buttonText}
                            </Button>
                            <ProtocolFileManager
                                companyId={activeCompany.id}
                                protocolId={protocolId!}
                                subjectId={subjectId}
                                workflow={workflow}
                                onFileCountChange={setServerFileCount}
                            />
                        </Box>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>
        )
    }

    const renderFilesView = () => {
        switch (workflow) {
            case "review":
            case "review_edit":
                return (
                    <Box>
                        {renderTitleBar()}
                        <ProtocolFileManager
                            companyId={activeCompany.id}
                            protocolId={protocolId!}
                            subjectId={subjectId}
                            workflow={workflow}
                            onFileCountChange={setServerFileCount}
                        />
                    </Box>
                )
            case "session":
                if (activeCompany && protocolId && isProtocolStep(step)) {
                    return renderSessionMode()
                }
                return
            default:
                return null;
        }
    }

    return (
        <Box style={{
            gridColumn: "1 / -1",
            display: "grid",
            grid: "auto/ 1fr",
            rowGap: "0.5em",
            alignItems: "center",
            width: "100%"
        }}
             className={stepClasses.stepViewField}>
            {renderFilesView()}
        </Box>
    );
};
